import { WebAppBookNow } from "@heal-me/healme-widget"
import React from "react"
import { twMerge } from "tailwind-merge"

import { practiceDefaultServiceId } from "../../@core/practice/practice.utils"
import FacebookIcon from "../../components/icons/FacebookIcon"
import InstagramIcon from "../../components/icons/InstagramIcon"
import LinkedinIcon from "../../components/icons/LinkedinIcon"
import TikTokIcon from "../../components/icons/TikTokIcon"
import XTwitterIcon from "../../components/icons/XTwitterIcon"
import YoutubeIcon from "../../components/icons/YoutubeIcon"
import { scrollToId } from "../ProProfile/ScrollToSection"
import { scopePath } from "../WebsiteBuilderV2/utils/urlUtils"

import HealMeVerifiedBadge from "./HealMeVerifiedBadge"

const Footer = ({ practice, theme, backgroundColor, color, viewingInBuilder }) => {
  backgroundColor ||= theme.colorPrimary
  color ||= theme.colorTextDark

  // Check if this is a v2 site - ONLY use explicit practice flags
  // Do NOT use isV2Site() as it does URL pattern matching that can incorrectly match v1 sites
  // This component is used by v1 sites, so we must be strict about v2 detection
  const isV2 = practice?.builderVersion === "v2" || practice?.builder_version === "v2"

  // Helper to handle navigation - use URLs for v2 sites, scrollToId for v1
  const handleNavigation = (e, target) => {
    if (isV2) {
      e.preventDefault()
      let path = "/"
      if (target === "home") {
        path = "/"
      } else if (target === "services") {
        path = "/services"
      } else if (target === "reviews") {
        path = "/review" // v2 sites use /review (singular)
      } else if (target === "about") {
        path = "/about"
      }
      window.location.href = scopePath(path, practice)
    } else {
      scrollToId(e, target)
    }
  }

  return (
    <div className="py-16 md:px-5" style={{ backgroundColor, color }}>
      <div className="container mx-auto flex items-center justify-between lg:flex-col lg:gap-14">
        <div>
          <div className="text-xl font-bold">{practice.user.name}</div>
          <div className="max-w-xl text-lg">{practice.user.title}</div>
        </div>
        <div className="flex gap-8 lg:flex-col md:w-full">
          {isV2 && (
            <a
              href={scopePath("/", practice)}
              className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
              style={{ "--hover-color": theme.colorAccent }}>
              Home
            </a>
          )}
          <button
            className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
            style={{ "--hover-color": theme.colorAccent }}
            onClick={(e) => handleNavigation(e, "about")}>
            About
          </button>
          <button
            className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
            style={{ "--hover-color": theme.colorAccent }}
            onClick={(e) => handleNavigation(e, "services")}>
            Services
          </button>
          <button
            className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
            style={{ "--hover-color": theme.colorAccent }}
            onClick={(e) => handleNavigation(e, "reviews")}>
            Reviews
          </button>
          <button
            className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
            style={{ "--hover-color": theme.colorAccent }}
            onClick={(e) => handleNavigation(e, "about")}>
            Credentials
          </button>

          {isV2 ? (
            <a
              href={scopePath("/services", practice)}
              className={twMerge(
                "inline-block border px-8 py-2 text-center transition duration-150 ease-out hover:opacity-90 md:w-full",
                practice.theme?.buttonStyle === "rounded" || theme?.buttonStyle === "rounded"
                  ? "rounded-full"
                  : "rounded",
                viewingInBuilder ? "pointer-events-none" : ""
              )}
              style={{
                backgroundColor: "#FFFFFF",
                color: theme.colorTextDark || "#333333",
                borderColor: theme.colorTextDark || "#333333"
              }}>
              Book Now
            </a>
          ) : (
            <WebAppBookNow
              practiceId={practice.id}
              buttonCopy="Book Now"
              serviceId={practiceDefaultServiceId(practice)}
              buttonClasses={twMerge(
                "border px-8 py-2 transition duration-150 ease-out hover:opacity-90 md:w-full",
                practice.theme?.buttonStyle === "rounded" || theme?.buttonStyle === "rounded"
                  ? "rounded-full"
                  : "rounded",
                viewingInBuilder ? "pointer-events-none" : ""
              )}
              style={{
                backgroundColor: "#FFFFFF",
                color: theme.colorTextDark || "#333333",
                borderColor: theme.colorTextDark || "#333333"
              }}
            />
          )}
        </div>
      </div>
      <div className="my-24 flex justify-center gap-8">
        {practice.instagram && (
          <a href={practice.instagram} className="flex justify-center" target="_blank" rel="noreferrer">
            <InstagramIcon height={32} width={32} color={color} />
          </a>
        )}
        {practice.facebook && (
          <a href={practice.facebook} className="flex justify-center" target="_blank" rel="noreferrer">
            <FacebookIcon height={32} width={24} color={color} />
          </a>
        )}
        {practice.tiktok && (
          <a href={practice.tiktok} className="flex justify-center" target="_blank" rel="noreferrer">
            <TikTokIcon height={32} width={32} color={color} />
          </a>
        )}
        {practice.linkedin && (
          <a href={practice.linkedin} className="flex justify-center" target="_blank" rel="noreferrer">
            <LinkedinIcon height={32} width={22} color={color} />
          </a>
        )}
        {practice.twitter && (
          <a href={practice.twitter} className="flex justify-center" target="_blank" rel="noreferrer">
            <XTwitterIcon height={32} width={21} color={color} />
          </a>
        )}
        {practice.youtube && (
          <a href={practice.youtube} className="flex justify-center" target="_blank" rel="noreferrer">
            <YoutubeIcon height={32} width={28} color={color} />
          </a>
        )}
      </div>
      {/* Hardcoded for pro */}
      {practice.id === 17868 && (
        <div className="container mx-auto mb-24 text-center text-sm italic">
          I respectfully acknowledge that we are residing and providing services on the traditional, unceded territory
          of the Wei Wai Kai First Nation, the traditional keepers of this land, and I honor their ancestral connection
          to this place
        </div>
      )}
      <div className="flex justify-center">
        <HealMeVerifiedBadge
          theme={theme}
          text="Powered by Heal.me"
          backgroundColor={backgroundColor}
          showBadgeIcon={false}
          color={color}
          link="https://heal.me"
        />
      </div>
    </div>
  )
}

export default Footer
